import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";

const RELEASE_NOTES_VERSION = "rpg-state-linking-2026-08";

function releaseNotesKey(user) {
  const identity = user?.id || user?.email || "current-user";
  return `ttt:release-notes:${RELEASE_NOTES_VERSION}:${identity}`;
}

export default function ReleaseNotesGate() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const storageKey = useMemo(() => (user ? releaseNotesKey(user) : null), [user]);

  useEffect(() => {
    if (loading || !storageKey) {
      setOpen(false);
      return;
    }
    try {
      setOpen(window.localStorage.getItem(storageKey) !== "seen");
    } catch {
      setOpen(false);
    }
  }, [loading, storageKey]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(storageKey, "seen");
    } catch {
      // Storage failures must not trap a player in the dialog.
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="release-notes-title">
      <section className="w-full max-w-lg overflow-hidden rounded-xl border border-amber-400/35 bg-[#17121b] shadow-2xl shadow-black/60">
        <div className="border-b border-amber-400/20 bg-[#211826] px-6 py-5">
          <p className="font-ui text-[10px] uppercase tracking-[0.28em] text-amber-300/80">TalesTalesTales update</p>
          <h2 id="release-notes-title" className="mt-2 font-display text-3xl text-amber-50">Your tale now remembers its rewards</h2>
        </div>
        <div className="space-y-3 px-6 py-5 font-body text-sm leading-relaxed text-amber-100/80">
          <p>Story rewards now stay connected to your character sheet instead of existing only in narration.</p>
          <ul className="list-disc space-y-2 pl-5 marker:text-amber-400">
            <li>Purchased and earned items can update inventory and gold.</li>
            <li>Recognised weapons, armour, and accessories can auto-equip into an empty slot.</li>
            <li>Equipped gear contributes visible stat, defence, and armour-class bonuses.</li>
            <li>Story effects and progression updates are surfaced more clearly in play.</li>
          </ul>
          <p className="text-xs text-amber-200/55">This note appears once for this account on this browser.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-amber-400/20 px-6 py-4">
          <a href="/changelog" className="px-3 py-2 font-ui text-xs uppercase tracking-wider text-amber-200/70 hover:text-amber-100">Full change log</a>
          <button type="button" onClick={dismiss} className="rounded-md border border-amber-400/60 bg-amber-400/15 px-4 py-2 font-ui text-xs uppercase tracking-wider text-amber-100 transition hover:bg-amber-400/25">Continue my tale</button>
        </div>
      </section>
    </div>
  );
}
